import React, { useState } from "react"
import { Button, Input, InputNumber } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowDown, faArrowUp, faTrash } from "@fortawesome/free-solid-svg-icons"
import { equalRanges, RangeItem, rangeName, rangeRefusal } from "../../model/ranges"

export interface RangeListEditorProps {
  /** The ranges, in the order the table or banner shows them. */
  ranges: RangeItem[]
  /** Gets the whole list after each edit. */
  onChange: (next: RangeItem[]) => void
  /** What a range becomes: a row of a table, or a column of a banner. Words the name rule. */
  owner?: "row" | "column"
  /**
   * The list, for screen readers. Every control's name starts with it, so
   * two editors on one screen are told apart.
   */
  label?: string
  /** The names of the other rows or columns beside the ranges, which no range can take. */
  others?: string[]
}

/** A finite number from a number field; anything else (empty) is no number. */
const numberOf = (v: number | string | null): number | undefined =>
  typeof v === "number" && Number.isFinite(v) ? v : undefined

/** A range's bounds in words, as a new range is named: "10 to 20", "10 or more", "up to 20". */
const boundsText = (r: RangeItem): string => rangeName(r.min, r.max)

const moved = (list: RangeItem[], from: number, to: number): RangeItem[] => {
  const next = [...list]
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item)
  return next
}

/**
 * A list of ranges of a number, each inclusive at both ends: the ranges in
 * order, each with its bounds, moved up or down and removed; "Add range"
 * with a from and a to value (either may be empty) and a name that follows
 * them ("10 to 20", "10 or more", "up to 20") until it is typed over; and
 * "Add equal ranges" from, to and step, whole numbers (0 to 100 by 20 gives
 * 0 to 20, 21 to 40 ... 81 to 100). A range is refused, with the reason
 * under the forms, for a name another range or another row or column
 * (`others`) has or the engine cannot take, no bound, or a from value above
 * its to value (`rangeRefusal`).
 */
const RangeListEditor: React.FC<RangeListEditorProps> = ({
  ranges,
  onChange,
  owner = "row",
  label = "Ranges",
  others = [],
}) => {
  const [from, setFrom] = useState<number | undefined>()
  const [to, setTo] = useState<number | undefined>()
  /** The name typed; null while it follows the bounds, again once it is emptied. */
  const [name, setName] = useState<string | null>(null)
  const [stepFrom, setStepFrom] = useState<number | undefined>()
  const [stepTo, setStepTo] = useState<number | undefined>()
  const [step, setStep] = useState<number | undefined>()
  const [error, setError] = useState<string | null>(null)
  const shownName = name ?? rangeName(from, to)
  /** A control's name for screen readers, after the list's: "Ranges of the table: Range from". */
  const named = (control: string): string => `${label}: ${control}`

  /** Writes the list, unless one of `added` is refused: then the first refusal is shown, and nothing is written. */
  const add = (added: RangeItem[]): boolean => {
    const names = [...others, ...ranges.map((r) => r.name)]
    for (const r of added) {
      const refused = rangeRefusal(r, names, owner)
      if (refused) {
        setError(refused)
        return false
      }
      names.push(r.name)
    }
    setError(null)
    onChange([...ranges, ...added])
    return true
  }

  const addRange = (): void => {
    const range: RangeItem = {
      name: shownName.trim(),
      ...(from !== undefined ? { min: from } : {}),
      ...(to !== undefined ? { max: to } : {}),
    }
    if (!add([range])) return
    setFrom(undefined)
    setTo(undefined)
    setName(null)
  }

  const addEqual = (): void => {
    if (stepFrom === undefined || stepTo === undefined || step === undefined) {
      setError("Give a from value, a to value and a step.")
      return
    }
    const made = equalRanges(stepFrom, stepTo, step)
    if (typeof made === "string") {
      setError(made)
      return
    }
    if (!add(made)) return
    setStepFrom(undefined)
    setStepTo(undefined)
    setStep(undefined)
  }

  return (
    <div className="ts-ranges">
      {ranges.length > 0 && (
        <ol className="ts-rows" aria-label={label}>
          {ranges.map((r, i) => {
            const bounds = boundsText(r)
            return (
              // Names are unique in a list the editor writes; the index keeps a stored double apart.
              <li key={`${r.name}:${i}`} className="ts-row">
                <span className="ts-row__name" title={r.name}>
                  {r.name}
                </span>
                {bounds !== r.name && <span className="ts-hint">{bounds}</span>}
                <div className="ts-row__controls">
                  <Button
                    type="text"
                    size="small"
                    aria-label={named(`Move ${r.name} up`)}
                    icon={<FontAwesomeIcon icon={faArrowUp} />}
                    disabled={i === 0}
                    onClick={() => onChange(moved(ranges, i, i - 1))}
                  />
                  <Button
                    type="text"
                    size="small"
                    aria-label={named(`Move ${r.name} down`)}
                    icon={<FontAwesomeIcon icon={faArrowDown} />}
                    disabled={i >= ranges.length - 1}
                    onClick={() => onChange(moved(ranges, i, i + 1))}
                  />
                  <Button
                    type="text"
                    size="small"
                    danger
                    aria-label={named(`Remove ${r.name}`)}
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    onClick={() => onChange(ranges.filter((_, k) => k !== i))}
                  />
                </div>
              </li>
            )
          })}
        </ol>
      )}
      <div className="ts-ranges__form" role="group" aria-label={named("Add range")}>
        <InputNumber
          size="small"
          className="ts-ranges__bound"
          aria-label={named("Range from")}
          placeholder="from"
          value={from ?? null}
          onChange={(v) => {
            setFrom(numberOf(v))
            setError(null)
          }}
        />
        <InputNumber
          size="small"
          className="ts-ranges__bound"
          aria-label={named("Range to")}
          placeholder="to"
          value={to ?? null}
          onChange={(v) => {
            setTo(numberOf(v))
            setError(null)
          }}
        />
        <Input
          size="small"
          className="ts-ranges__name"
          aria-label={named("Range name")}
          placeholder="Name"
          value={shownName}
          onChange={(e) => {
            // Emptied, the name follows the bounds again.
            setName(e.target.value === "" ? null : e.target.value)
            setError(null)
          }}
          onPressEnter={addRange}
        />
        <Button size="small" aria-label={named("Add range")} onClick={addRange}>
          Add range
        </Button>
      </div>
      <div className="ts-ranges__form" role="group" aria-label={named("Add equal ranges")}>
        <InputNumber
          size="small"
          className="ts-ranges__bound"
          aria-label={named("Equal ranges from")}
          placeholder="from"
          value={stepFrom ?? null}
          onChange={(v) => {
            setStepFrom(numberOf(v))
            setError(null)
          }}
        />
        <InputNumber
          size="small"
          className="ts-ranges__bound"
          aria-label={named("Equal ranges to")}
          placeholder="to"
          value={stepTo ?? null}
          onChange={(v) => {
            setStepTo(numberOf(v))
            setError(null)
          }}
        />
        <InputNumber
          size="small"
          className="ts-ranges__bound"
          aria-label={named("Equal ranges step")}
          placeholder="step"
          value={step ?? null}
          onChange={(v) => {
            setStep(numberOf(v))
            setError(null)
          }}
        />
        <Button size="small" aria-label={named("Add equal ranges")} onClick={addEqual}>
          Add equal ranges
        </Button>
      </div>
      {error && (
        <p className="ts-error" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

export default RangeListEditor
